import React, { useDeferredValue, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/hooks/useToast';
import { 
  AlertCircle,
  BarChart3, 
  Download, 
  FileText, 
  Filter, 
  Loader2,
  Plus, 
  RefreshCw, 
  Calendar,
  Search,
  TrendingUp,
  Users,
  Package,
  DollarSign,
  Clock,
  Play,
  Eye,
  Trash2,
  X
} from 'lucide-react';
import { format, formatDistanceToNow, subDays, subMonths } from 'date-fns';
import ReportBuilder from '@/components/reports/ReportBuilder';
import ReportVisualization from '@/components/reports/ReportVisualization';
import ReportScheduler from '@/components/reports/ReportScheduler';
import { reportService, Report, ReportExecution } from '@/services/reportService';

type ReportStatusFilter = 'all' | 'NOT_RUN' | ReportExecution['status'];
type ReportTypeFilter = 'all' | Report['type'];
type ReportFormatFilter = 'all' | Report['format'];

const REPORT_TYPE_LABELS: Record<Report['type'], string> = {
  OPERATIONAL: 'Operational',
  FINANCIAL: 'Financial',
  CUSTOMER_ANALYTICS: 'Customer Analytics',
  HUB_PERFORMANCE: 'Hub Performance'
};

const REPORT_FORMAT_LABELS: Record<Report['format'], string> = {
  EXCEL: 'Excel',
  CSV: 'CSV',
  JSON: 'JSON',
  PDF: 'PDF'
};

const REPORT_STATUS_LABELS: Record<ReportExecution['status'], string> = {
  COMPLETED: 'Completed',
  PROCESSING: 'Processing',
  FAILED: 'Failed'
};

const REPORT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'OPERATIONAL', label: REPORT_TYPE_LABELS.OPERATIONAL },
  { value: 'FINANCIAL', label: REPORT_TYPE_LABELS.FINANCIAL },
  { value: 'CUSTOMER_ANALYTICS', label: REPORT_TYPE_LABELS.CUSTOMER_ANALYTICS },
  { value: 'HUB_PERFORMANCE', label: REPORT_TYPE_LABELS.HUB_PERFORMANCE }
];

const REPORT_FORMAT_OPTIONS = [
  { value: 'all', label: 'All Formats' },
  { value: 'EXCEL', label: REPORT_FORMAT_LABELS.EXCEL },
  { value: 'CSV', label: REPORT_FORMAT_LABELS.CSV },
  { value: 'JSON', label: REPORT_FORMAT_LABELS.JSON },
  { value: 'PDF', label: REPORT_FORMAT_LABELS.PDF }
];

const REPORT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'COMPLETED', label: REPORT_STATUS_LABELS.COMPLETED },
  { value: 'PROCESSING', label: REPORT_STATUS_LABELS.PROCESSING },
  { value: 'FAILED', label: REPORT_STATUS_LABELS.FAILED },
  { value: 'NOT_RUN', label: 'Not Run' }
];

const REPORT_FORMAT_EXTENSION: Record<Report['format'], string> = {
  EXCEL: 'xlsx',
  CSV: 'csv',
  JSON: 'json',
  PDF: 'pdf'
};

const DATE_PRESETS = [
  {
    key: 'last-7',
    label: 'Last 7 Days',
    getRange: () => ({
      from: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd')
    })
  },
  {
    key: 'last-30',
    label: 'Last 30 Days',
    getRange: () => ({
      from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd')
    })
  },
  {
    key: 'last-month',
    label: 'Last Month',
    getRange: () => ({
      from: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd')
    })
  }
];

const getExecutionTimestamp = (execution: ReportExecution): number => {
  const timeValue = execution.endTime || execution.startTime;
  const parsed = Date.parse(timeValue);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getLatestExecution = (executions?: ReportExecution[]): ReportExecution | undefined => {
  if (!executions || executions.length === 0) return undefined;
  return executions.reduce((latest, current) =>
    getExecutionTimestamp(current) > getExecutionTimestamp(latest) ? current : latest
  );
};

const formatExecutionTime = (timestamp?: string): string => {
  if (!timestamp) return 'Not run yet';
  const parsed = Date.parse(timestamp);
  if (Number.isNaN(parsed)) return 'Unknown';
  return formatDistanceToNow(parsed, { addSuffix: true });
};

const buildSearchValue = (report: Report): string =>
  [report.name, report.description, ...(report.tags || [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getReportTypeIcon = (type: Report['type']) => {
  switch (type) {
    case 'OPERATIONAL':
      return <Package className="h-4 w-4" />;
    case 'FINANCIAL':
      return <DollarSign className="h-4 w-4" />;
    case 'CUSTOMER_ANALYTICS':
      return <Users className="h-4 w-4" />;
    case 'HUB_PERFORMANCE':
      return <TrendingUp className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const getStatusBadgeClass = (status?: ReportExecution['status']) => {
  if (!status) {
    return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200';
  }
  switch (status) {
    case 'COMPLETED':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200';
    case 'PROCESSING':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200';
    case 'FAILED':
      return 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200';
  }
};

const ReportsAnalytics = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd')
  });
  const [reportFilters, setReportFilters] = useState<{
    query: string;
    type: ReportTypeFilter;
    format: ReportFormatFilter;
    status: ReportStatusFilter;
  }>({
    query: '',
    type: 'all',
    format: 'all',
    status: 'all'
  });
  const [generatingReportId, setGeneratingReportId] = useState<string | null>(null);
  const [downloadingExecutionId, setDownloadingExecutionId] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(reportFilters.query);

  // Fetch reports
  const { 
    data: reportsData, 
    isLoading: isReportsLoading, 
    refetch: refetchReports 
  } = useQuery({
    queryKey: ['reports'],
    queryFn: () => reportService.getReports({ page: 1, limit: 50 })
  });

  // Fetch report templates
  const { 
    data: templatesData, 
    isLoading: isTemplatesLoading 
  } = useQuery({
    queryKey: ['report-templates'],
    queryFn: () => reportService.getReportTemplates()
  });

  const reports = reportsData?.data || [];
  const templates = templatesData?.data || [];
  const totalReportsCount = reportsData?.total ?? reports.length;
  const totalTemplatesCount = templatesData?.total ?? templates.length;
  const normalizedQuery = useMemo(() => deferredQuery.trim().toLowerCase(), [deferredQuery]);
  const showStatusFilter = activeTab === 'reports';
  const isDateRangeInvalid = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return false;
    const fromTime = Date.parse(dateRange.from);
    const toTime = Date.parse(dateRange.to);
    if (Number.isNaN(fromTime) || Number.isNaN(toTime)) return false;
    return fromTime > toTime;
  }, [dateRange.from, dateRange.to]);

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (normalizedQuery && !buildSearchValue(report).includes(normalizedQuery)) {
        return false;
      }
      if (reportFilters.type !== 'all' && report.type !== reportFilters.type) {
        return false;
      }
      if (reportFilters.format !== 'all' && report.format !== reportFilters.format) {
        return false;
      }
      if (reportFilters.status !== 'all') {
        const latestExecution = getLatestExecution(report.executions);
        if (reportFilters.status === 'NOT_RUN') {
          return !latestExecution;
        }
        return latestExecution?.status === reportFilters.status;
      }
      return true;
    });
  }, [normalizedQuery, reportFilters.format, reportFilters.status, reportFilters.type, reports]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      if (normalizedQuery && !buildSearchValue(template).includes(normalizedQuery)) {
        return false;
      }
      if (reportFilters.type !== 'all' && template.type !== reportFilters.type) {
        return false;
      }
      if (reportFilters.format !== 'all' && template.format !== reportFilters.format) {
        return false;
      }
      return true;
    });
  }, [normalizedQuery, reportFilters.format, reportFilters.type, templates]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(reportFilters.query.trim()) ||
      reportFilters.type !== 'all' ||
      reportFilters.format !== 'all' ||
      (showStatusFilter && reportFilters.status !== 'all');
  }, [reportFilters.format, reportFilters.query, reportFilters.status, reportFilters.type, showStatusFilter]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; value: string; onClear: () => void }> = [];
    if (reportFilters.query.trim()) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: reportFilters.query.trim(),
        onClear: () => setReportFilters((prev) => ({ ...prev, query: '' }))
      });
    }
    if (reportFilters.type !== 'all') {
      const label = REPORT_TYPE_OPTIONS.find((option) => option.value === reportFilters.type)?.label || reportFilters.type;
      chips.push({
        id: 'type',
        label: 'Type',
        value: label,
        onClear: () => setReportFilters((prev) => ({ ...prev, type: 'all' }))
      });
    }
    if (reportFilters.format !== 'all') {
      const label = REPORT_FORMAT_OPTIONS.find((option) => option.value === reportFilters.format)?.label || reportFilters.format;
      chips.push({
        id: 'format',
        label: 'Format',
        value: label,
        onClear: () => setReportFilters((prev) => ({ ...prev, format: 'all' }))
      });
    }
    if (showStatusFilter && reportFilters.status !== 'all') {
      const label = REPORT_STATUS_OPTIONS.find((option) => option.value === reportFilters.status)?.label || reportFilters.status;
      chips.push({
        id: 'status',
        label: 'Status',
        value: label,
        onClear: () => setReportFilters((prev) => ({ ...prev, status: 'all' }))
      });
    }
    return chips;
  }, [reportFilters.format, reportFilters.query, reportFilters.status, reportFilters.type, showStatusFilter]);

  const clearReportFilters = () => {
    setReportFilters({
      query: '',
      type: 'all',
      format: 'all',
      status: 'all'
    });
  };

  const buildReportFilename = (report: Report, executionId: string) => {
    const extension = REPORT_FORMAT_EXTENSION[report.format] || report.format.toLowerCase();
    const safeName = report.name
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'report';
    return `${safeName}-${executionId}.${extension}`;
  };

  const handleDownloadReport = async (report: Report, executionId: string) => {
    try {
      setDownloadingExecutionId(executionId);
      const blob = await reportService.downloadReport(report._id, executionId);
      reportService.downloadFile(blob, buildReportFilename(report, executionId));
    } catch (error: any) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Unable to download the report file.',
        variant: 'destructive'
      });
    } finally {
      setDownloadingExecutionId(null);
    }
  };

  // Generate report mutation
  const generateReportMutation = useMutation({
    mutationFn: ({ reportId, options }: { reportId: string; report: Report; options: any }) =>
      reportService.generateReport(reportId, options),
    onMutate: ({ reportId }: { reportId: string; report: Report; options: any }) => {
      setGeneratingReportId(reportId);
    },
    onSuccess: (data, variables) => {
      const executionId = data?.executionId;
      const reportForDownload = variables?.report;
      const action = executionId && reportForDownload
        ? (
          <ToastAction
            altText="Download report"
            onClick={() => handleDownloadReport(reportForDownload, executionId)}
          >
            Download
          </ToastAction>
        )
        : undefined;
      toast({
        title: 'Report Generated',
        description: 'Your report has been generated successfully.',
        action
      });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate report.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setGeneratingReportId(null);
    }
  });

  // Delete report mutation
  const deleteReportMutation = useMutation({
    mutationFn: (reportId: string) => reportService.deleteReport(reportId),
    onSuccess: () => {
      toast({
        title: 'Report Deleted',
        description: 'Report has been deleted successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete report.',
        variant: 'destructive',
      });
    },
  });

  const handleGenerateReport = (report: Report) => {
    if (isDateRangeInvalid) {
      toast({
        title: 'Invalid Date Range',
        description: 'The start date must be before the end date.',
        variant: 'destructive',
      });
      return;
    }
    generateReportMutation.mutate({
      reportId: report._id,
      report,
      options: {
        format: report.format,
        filters: {
          ...report.filters,
          dateFrom: dateRange.from,
          dateTo: dateRange.to
        },
        includeSummary: true
      }
    });
  };

  const handleDeleteReport = (reportId: string) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      deleteReportMutation.mutate(reportId);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground">
            Generate insights and monitor performance with comprehensive reporting
          </p>
        </div>
        
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button
            onClick={() => setShowReportBuilder(true)}
            className="flex w-full items-center gap-2 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            New Report
          </Button>
          <Button
            variant="outline"
            onClick={() => refetchReports()}
            className="flex w-full items-center gap-2 sm:w-auto"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Global Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid gap-3 sm:grid-cols-2 lg:max-w-xl">
              <div className="space-y-1">
                <Label htmlFor="dateFrom">From Date</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dateTo">To Date</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="h-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.key}
                  variant="outline"
                  size="sm"
                  onClick={() => setDateRange(preset.getRange())}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
          {isDateRangeInvalid && (
            <div className="mt-3 flex items-center gap-2 text-sm text-rose-600">
              <AlertCircle className="h-4 w-4" />
              End date must be later than the start date.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 gap-1 h-auto sm:grid-cols-4">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="reports">My Reports</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
        </TabsList>

        {(activeTab === 'reports' || activeTab === 'templates') && (
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-lg">Report Filters</CardTitle>
                  <CardDescription>Search and refine saved reports and templates.</CardDescription>
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearReportFilters}
                    className="w-full sm:w-auto"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`grid gap-4 ${showStatusFilter ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} sm:grid-cols-2`}>
                <div className="space-y-1">
                  <Label htmlFor="reportSearch">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="reportSearch"
                      placeholder="Search by name, description, or tags"
                      value={reportFilters.query}
                      onChange={(e) => setReportFilters((prev) => ({ ...prev, query: e.target.value }))}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape' && reportFilters.query) {
                          event.preventDefault();
                          setReportFilters((prev) => ({ ...prev, query: '' }));
                        }
                      }}
                      className="h-10 pl-10 pr-9"
                    />
                    {reportFilters.query && (
                      <button
                        type="button"
                        onClick={() => setReportFilters((prev) => ({ ...prev, query: '' }))}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select
                    value={reportFilters.type}
                    onValueChange={(value) => setReportFilters((prev) => ({ ...prev, type: value as ReportTypeFilter }))}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Format</Label>
                  <Select
                    value={reportFilters.format}
                    onValueChange={(value) => setReportFilters((prev) => ({ ...prev, format: value as ReportFormatFilter }))}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="All Formats" />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_FORMAT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {showStatusFilter && (
                  <div className="space-y-1">
                    <Label>Status</Label>
                    <Select
                      value={reportFilters.status}
                      onValueChange={(value) => setReportFilters((prev) => ({ ...prev, status: value as ReportStatusFilter }))}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        {REPORT_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {hasActiveFilters && activeFilterChips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Active filters
                  </span>
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="gap-1 pr-1 text-xs font-medium">
                      <span className="max-w-[220px] truncate" title={`${chip.label}: ${chip.value}`}>
                        {chip.label}: {chip.value}
                      </span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/70"
                        aria-label={`Clear ${chip.label}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Analytics Dashboard */}
        <TabsContent value="dashboard" className="space-y-6">
          <ReportVisualization dateRange={dateRange} />
        </TabsContent>

        {/* My Reports */}
        <TabsContent value="reports" className="space-y-6">
          <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>
              {isReportsLoading
                ? 'Loading reports…'
                : `Showing ${filteredReports.length} of ${totalReportsCount} reports`}
            </span>
            {hasActiveFilters && (
              <Badge variant="secondary">
                {activeFilterChips.length} filter{activeFilterChips.length !== 1 ? 's' : ''} active
              </Badge>
            )}
          </div>
          <div className="grid gap-4">
            {isReportsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-3 bg-muted rounded w-1/2"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="h-20 bg-muted rounded"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredReports.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredReports.map((report: Report) => {
                  const latestExecution = getLatestExecution(report.executions);
                  const lastRunLabel = formatExecutionTime(latestExecution?.endTime || latestExecution?.startTime);
                  const isGenerating = generatingReportId === report._id && generateReportMutation.isPending;
                  const canDownload = Boolean(latestExecution?.executionId && latestExecution.status === 'COMPLETED');
                  const isDownloading = Boolean(
                    canDownload && latestExecution?.executionId && downloadingExecutionId === latestExecution.executionId
                  );

                  return (
                    <Card key={report._id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {getReportTypeIcon(report.type)}
                            <CardTitle className="text-lg">{report.name}</CardTitle>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedReport(report)}
                              aria-label="View report"
                              title="View report"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteReport(report._id)}
                              aria-label="Delete report"
                              title="Delete report"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <CardDescription>{report.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Type:</span>
                            <Badge variant="outline">{REPORT_TYPE_LABELS[report.type]}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Format:</span>
                            <Badge variant="outline">{REPORT_FORMAT_LABELS[report.format]}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Last Run:</span>
                            <span className="text-foreground">{lastRunLabel}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge className={getStatusBadgeClass(latestExecution?.status)}>
                              {latestExecution ? REPORT_STATUS_LABELS[latestExecution.status] : 'Not Run'}
                            </Badge>
                          </div>
                          {typeof latestExecution?.recordsProcessed === 'number' && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Records:</span>
                              <span className="font-medium text-foreground">{latestExecution.recordsProcessed}</span>
                            </div>
                          )}
                          {report.tags?.length ? (
                            <div className="flex flex-wrap gap-1">
                              {report.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {report.tags.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{report.tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          ) : null}
                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button
                              size="sm"
                              onClick={() => handleGenerateReport(report)}
                              disabled={isGenerating || isDateRangeInvalid}
                              className="flex-1 min-w-[140px]"
                            >
                              {isGenerating ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Play className="h-4 w-4 mr-1" />
                              )}
                              {isGenerating ? 'Generating' : 'Generate'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedReport(report);
                                setShowScheduler(true);
                              }}
                              aria-label="Schedule report"
                              title="Schedule report"
                            >
                              <Clock className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!canDownload || isDownloading}
                              onClick={() => {
                                if (latestExecution?.executionId) {
                                  handleDownloadReport(report, latestExecution.executionId);
                                }
                              }}
                              aria-label="Download latest report"
                              title={canDownload ? 'Download latest report' : 'No completed report to download'}
                            >
                              {isDownloading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : reports.length > 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Reports Match Filters</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or clearing them to see all reports.
                  </p>
                  <Button variant="outline" onClick={clearReportFilters}>
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Reports Found</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    Create your first report to get started with analytics
                  </p>
                  <Button onClick={() => setShowReportBuilder(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Report
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Report Templates */}
        <TabsContent value="templates" className="space-y-6">
          <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>
              {isTemplatesLoading
                ? 'Loading templates…'
                : `Showing ${filteredTemplates.length} of ${totalTemplatesCount} templates`}
            </span>
            {hasActiveFilters && (
              <Badge variant="secondary">
                {activeFilterChips.length} filter{activeFilterChips.length !== 1 ? 's' : ''} active
              </Badge>
            )}
          </div>
          <div className="grid gap-4">
            {isTemplatesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-3 bg-muted rounded w-1/2"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="h-20 bg-muted rounded"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map((template: Report) => {
                  const isGenerating = generatingReportId === template._id && generateReportMutation.isPending;
                  return (
                    <Card key={template._id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                          {getReportTypeIcon(template.type)}
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                        </div>
                        <CardDescription>{template.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Type:</span>
                            <Badge variant="outline">{REPORT_TYPE_LABELS[template.type]}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Format:</span>
                            <Badge variant="outline">{REPORT_FORMAT_LABELS[template.format]}</Badge>
                          </div>
                          {template.tags?.length ? (
                            <div className="flex flex-wrap gap-1">
                              {template.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {template.tags.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{template.tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          ) : null}
                          <Button
                            size="sm"
                            onClick={() => handleGenerateReport(template)}
                            disabled={isGenerating || isDateRangeInvalid}
                            className="w-full"
                          >
                            {isGenerating ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4 mr-1" />
                            )}
                            {isGenerating ? 'Generating' : 'Use Template'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : templates.length > 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Templates Match Filters</h3>
                  <p className="text-muted-foreground mb-4">
                    Adjust your filters or clear them to see all templates.
                  </p>
                  <Button variant="outline" onClick={clearReportFilters}>
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Templates Available</h3>
                  <p className="text-muted-foreground text-center">
                    Report templates will appear here when available
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Scheduled Reports */}
        <TabsContent value="scheduled" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Scheduled Reports
              </CardTitle>
              <CardDescription>
                Manage automated report generation and distribution
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Scheduled Reports</h3>
                <p className="text-muted-foreground mb-4">
                  Set up automated report generation to receive regular insights
                </p>
                <Button onClick={() => setShowScheduler(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Report
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Report Builder Modal */}
      {showReportBuilder && (
        <ReportBuilder
          onClose={() => setShowReportBuilder(false)}
          onSave={() => {
            setShowReportBuilder(false);
            refetchReports();
          }}
        />
      )}

      {/* Report Scheduler Modal */}
      {showScheduler && selectedReport && (
        <ReportScheduler
          report={selectedReport}
          onClose={() => {
            setShowScheduler(false);
            setSelectedReport(null);
          }}
          onSave={() => {
            setShowScheduler(false);
            setSelectedReport(null);
            refetchReports();
          }}
        />
      )}
    </div>
  );
};

export default ReportsAnalytics;
